import { Controller } from '@hotwired/stimulus'
import { DeviceDetection } from '../lib/device_detection'
import { FirebaseNotificationManager } from '../notifications/firebase_notifications'

export default class extends Controller {
  static targets = [
    'userAgent',
    'platform',
    'displayMode',
    'browser',
    'isSecureContext',
    'hasServiceWorkerSupport',
    'hasNotificationSupport',
    'hasPushManagerInWindow',
    'notificationPermission',
    'firebaseSupported',
    'hasFirebaseToken'
  ]

  connect () {
    this.updateClientEnvironment()
  }

  async updateClientEnvironment () {
    // User Agent
    this.userAgentTarget.textContent = DeviceDetection.getUserAgent()

    // Platform (consolidated)
    this.platformTarget.textContent = this.detectPlatform()

    // Display Mode
    this.displayModeTarget.textContent = this.detectDisplayMode()

    // Browser (consolidated)
    this.browserTarget.textContent = this.detectBrowser()

    // Capability checks
    this.isSecureContextTarget.textContent = this.formatBoolean(
      DeviceDetection.isSecureContext()
    )
    this.hasServiceWorkerSupportTarget.textContent = this.formatBoolean(
      DeviceDetection.hasServiceWorkerSupport()
    )
    this.hasNotificationSupportTarget.textContent = this.formatBoolean(
      DeviceDetection.hasNotificationSupport()
    )
    this.hasPushManagerInWindowTarget.textContent = this.formatBoolean(
      DeviceDetection.hasPushManagerInWindow()
    )

    // Firebase-specific checks
    this.notificationPermissionTarget.textContent =
      FirebaseNotificationManager.getPermissionStatus()

    const supported = FirebaseNotificationManager.isSupported()
    if (supported === true) {
      this.firebaseSupportedTarget.textContent = 'Yes'
      this.firebaseSupportedTarget.classList.add('text-green-700')
    } else {
      this.firebaseSupportedTarget.textContent = `No: ${supported}`
      this.firebaseSupportedTarget.classList.add('text-red-600')
    }

    // Check for Firebase token (async)
    try {
      console.log('🎯 [Debug] About to sync Firebase token...')
      const ecosystemId = document.querySelector('meta[name="ecosystem_id"]')?.content
      const result = await FirebaseNotificationManager.syncToken(ecosystemId)
      if (result.orphanCleaned) {
        console.log('🧹 [Debug] Orphaned Firebase token cleaned during debug check')
      }
      console.log('🎯 [Debug] Firebase sync complete, checking token...')
      const hasToken = await FirebaseNotificationManager.hasTokenForEcosystem(ecosystemId)
      console.log('🎯 [Debug] Final hasToken result:', hasToken)
      this.hasFirebaseTokenTarget.textContent = this.formatBoolean(hasToken)
    } catch (err) {
      this.hasFirebaseTokenTarget.textContent = `Error: ${err.message}`
      this.hasFirebaseTokenTarget.classList.add('text-red-600')
    }
  }

  detectBrowser () {
    if (DeviceDetection.isChromeDesktop()) {
      return 'Chrome Desktop'
    } else if (DeviceDetection.isFirefoxDesktop()) {
      return 'Firefox Desktop'
    } else if (DeviceDetection.isSafariDesktop()) {
      return 'Safari Desktop'
    }
    return 'N/A'
  }

  detectDisplayMode () {
    return DeviceDetection.isPWA() ? 'PWA' : 'Browser'
  }

  detectPlatform () {
    const iosVersion = DeviceDetection.getIOSVersion()
    if (iosVersion) {
      return `iOS ${iosVersion.string}`
    }

    if (DeviceDetection.isAndroid()) {
      return 'Android'
    }

    if (DeviceDetection.isMobileDevice()) {
      return 'Mobile (Unknown)'
    }

    return 'Desktop'
  }

  formatBoolean (value) {
    return value ? 'Yes' : 'No'
  }
}
